import CloseIcon from "@mui/icons-material/Close";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListSubheader from "@mui/material/ListSubheader";
import { styled } from "@mui/material/styles";
import clsx from "clsx";
import { getLocalizedGroupLabel } from "csi/store/common";
import { Box, Button, FlexColumn, IconButton, OverflowText, Text } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { black, grey, white } from "designSystem/styles/color";
import { FONT_SEMIBOLD } from "designSystem/theme";
import { observer } from "mobx-react-lite";
import React, { Fragment, FunctionComponent } from "react";
import { IAggregateStoreResult } from "src/c8-platform/type/aggregateStore";
import { C8NodeType } from "src/c8-platform/type/node";
import { IGoToDialogGroup, IGoToDialogTab, useGoToDialogStore } from "./GoToDialogStore";
import SuggestedChip from "./SuggestedChip";

const PREFIX = "SelectionOverviewPane";

const classes = {
	list: `${PREFIX}list`,
	subheader: `${PREFIX}subheader`,
	listItem: `${PREFIX}listItem`,
	listItemSlim: `${PREFIX}listItemSlim`,
	listItemGroupHeader: `${PREFIX}listItemGroupHeader`,
	listItemGroupHeaderText: `${PREFIX}listItemGroupHeaderText`,
	itemText: `${PREFIX}itemText`,
	title: `${PREFIX}title`,
	noSelectionText: `${PREFIX}noSelectionText`,
	footer: `${PREFIX}footer`,
};

const StyledBox = styled(Box)(({
	theme,
}) => ({
	backgroundColor: grey[200],
	height: "100%",
	display: "flex",
	flexDirection: "column",

	[`& .${classes.list}`]: {
		width: "100%",
		overflow: "auto",
		position: "relative",
		marginBottom: 16,
		"& ul": {
			padding: "0 16px",
		},
	},
	[`& .${classes.subheader}`]: {
		alignItems: "center",
		backgroundColor: grey[200],
		display: "flex",
		top: 0,
		lineHeight: "26px",
		padding: 0,
	},
	[`& .${classes.listItem}`]: {
		background: white[50],
		margin: "8px 0",
		padding: "3px 4px 3px 8px",
		borderRadius: 3,
	},
	[`& .${classes.listItemSlim}`]: {
		alignItems: "flex-start",
		paddingTop: "8px",
		paddingBottom: "8px",

		"& .MuiListItemText-primary": {
			color: black[100],
			fontWeight: FONT_SEMIBOLD,
		},
		"& .MuiListItemText-secondary": {
			fontSize: "12px",
			lineHeight: "14px",
		},
		"& .MuiIconButton-root": {
			alignItems: "flex-start",
			top: "-2px",
		},
	},
	[`& .${classes.listItemGroupHeader}`]: {
		alignItems: "center",
		background: "none",
		display: "flex",
		marginBottom: "-2px",
		padding: 0,
	},
	[`& .${classes.listItemGroupHeaderText}`]: {
		background: grey[500],
		borderRadius: 3,
		height: "17px",
		lineHeight: "9px",
		padding: "4px",
	},
	[`& .${classes.itemText}`]: {
		"& span": {
			overflow: "hidden",
			textOverflow: "ellipsis",
			whiteSpace: "nowrap",
		},
	},
	[`& .${classes.title}`]: {
		padding: "19px 16px",
	},
	[`& .${classes.noSelectionText}`]: {
		color: black[500],
		paddingLeft: 16,
	},
	[`& .${classes.footer}`]: {
		boxShadow: `inset 0px 2px 0px ${grey[400]}`,
		margin: 16,
		marginTop: 0,
		paddingTop: 16,
		display: "flex",
		alignItems: "center",
		"& span": {
			flex: 1,
		},
	},
}));

export const SelectionOverviewPaneClosed = styled("div")(({
	theme,
}) => ({
	backgroundColor: grey[200],
	height: "100%",
}));

interface SelectionItemProps {
	/** whether selection item is invalid */
	invalid?: boolean;

	/** selection item to render */
	item: IAggregateStoreResult;

	/** result node type */
	nodeType?: C8NodeType;
}

interface SelectionItemsListProps {
	/** indicates if results in section are invalid */
	invalid?: boolean;

	/** result node type */
	nodeType?: C8NodeType;

	/** selections to render */
	selections: IAggregateStoreResult[];
}

interface SelectionItemSectionProps {
	/** indicates if selections are grouped */
	grouped: boolean;

	/** groups information */
	groups: IGoToDialogGroup[];

	/** tab's label to render */
	label: string;

	/** selections to render */
	selections: IAggregateStoreResult[][];

	/** whether tab is suggested */
	suggested?: boolean;
}

interface SelectionOverviewTabProps {
	/** tab holding selections to render */
	tab: IGoToDialogTab;
}

interface OverflowMultiTextProps {
	/** primary text to display */
	primary: string;

	/** secondary text to display */
	secondary: string;
}

const OverflowMultiText: FunctionComponent<OverflowMultiTextProps> = (props) => {
	const { primary, secondary } = props;

	return (
		<>
			<OverflowText semiBold text={primary} />
			{secondary && (<OverflowText text={secondary} />)}
		</>
	);
};

const SelectionItem: FunctionComponent<SelectionItemProps> = observer(({ invalid, item, nodeType }) => {
	const { changeSelection, getResultText } = useGoToDialogStore();
	const loc = useLocale();
	const [primaryName, secondaryName] = getResultText(loc, item, nodeType);

	return (
		<ListItem 
			className={clsx(classes.listItem, secondaryName && classes.listItemSlim)}
			data-csi-automation="SelectionOverviewListItem"
		>
			<FlexColumn gap={1} width="90%">
				<OverflowMultiText primary={primaryName} secondary={secondaryName} />
			</FlexColumn>
			{invalid && (
				<Text
					className="csi-invalid-selection-chip"
					marginRight={4}
					res="Invalid"
				/>
			)}
			<IconButton
				onClick={() => { changeSelection(item.$id, false); }}
				size="small"
			>
				<CloseIcon />
			</IconButton>
		</ListItem>
	);
});

const SelectionItemsList: FunctionComponent<SelectionItemsListProps> = ({ invalid, nodeType, selections }) => {
	return (
		<>
			{selections.map((item) => (
				<SelectionItem
					invalid={invalid}
					item={item}
					key={item.$id}
					nodeType={nodeType}
				/>
			))}
		</>
	);
};

const SelectionItemSection: FunctionComponent<SelectionItemSectionProps> = ({ grouped, groups, label, selections, suggested }) => {
	const { listItem, listItemGroupHeader, listItemGroupHeaderText, subheader } = classes;

	return (
		<li>
			<ul>
				{!!label && (
					<ListSubheader className={subheader}>
						{label}
						{!!suggested &&
							<SuggestedChip slim />
						}
					</ListSubheader>
				)}
				{grouped ?
					selections.map((groupSelections, index) => {
						const { aggregateGroup } = groups[index];

						return groupSelections.length ? (
							<Fragment key={index}>
								<ListItem className={`${listItem} ${listItemGroupHeader}`}>
									<OverflowText
										className={listItemGroupHeaderText}
										text={getLocalizedGroupLabel(aggregateGroup)}
									/>
									{!!aggregateGroup.suggested &&
										<SuggestedChip slim />
									}
								</ListItem>
								<SelectionItemsList
									nodeType={aggregateGroup?.nodeType}
									selections={groupSelections}
								/>
							</Fragment>
						)
							:
							null;
					})
					: (
						<SelectionItemsList
							nodeType={groups[0].nodeType}
							selections={selections[0]}
						/>
					)}
			</ul>
		</li>
	);
};

const SelectionOverviewTab: FunctionComponent<SelectionOverviewTabProps> = observer(({ tab }) => {
	const { getTabLabel, getTabSelections, hasTabs, isTabSuggested } = useGoToDialogStore();
	const loc = useLocale();
	const label = getTabLabel(tab, loc);
	const selections = getTabSelections(tab, loc);
	const suggested = hasTabs && isTabSuggested(tab);
	const { grouped, groups } = tab;

	return !!selections.length && (
		<SelectionItemSection
			grouped={grouped}
			groups={groups}
			label={label}
			selections={selections}
			suggested={suggested}
		/>
	);
});

const SelectionOverviewPane: FunctionComponent = observer(() => {
	const { clearSelection, invalidResults, isFirstViewLoaded, numberOfSelections, tabs } = useGoToDialogStore();
	return (
		<StyledBox data-csi-automation="SelectionOverviewPane">
			<Box className={classes.title}>
				<Text res="DialogSelectionOverview" variant="h4" />
			</Box>
			{!!numberOfSelections && (
				<Fragment>
					<List
						className={classes.list}
						data-csi-automation="SelectionOverviewList"
						subheader={<li />}
					>
						{!!invalidResults.length && (
							<li>
								<ul>
									<SelectionItemsList
										invalid
										selections={invalidResults}
									/>
								</ul>
							</li>
						)}
						{
							tabs.map((tab, i) => (
								<SelectionOverviewTab key={i} tab={tab} />
							))
						}
					</List>
					<Box 
						className={classes.footer}
						data-csi-automation="SelectionOverviewCount"
					>
						<Text
							res="DialogSelectionCount"
							resParams={[numberOfSelections]}
							variant="h5"
						/>
						<Button
							design="link"
							onClick={clearSelection}
							res="ClearAll"
						/>
					</Box>
				</Fragment>
			)}
			{!numberOfSelections && isFirstViewLoaded && (
				<Text
					bold
					className={classes.noSelectionText}
					res="DialogSelectionEmpty"
				/>
			)}
		</StyledBox>
	);
});

export default SelectionOverviewPane;